<template>
  <main role="main">
    <div class="container-xxl py-4">
      <div class="d-flex flex-row align-items-center justify-content-between mb-4">
        <h4 class="m-0">{{ $t("contactList") }}</h4>
        <div>
          <b-button class="mr-4" variant="outline-secondary" @click="onClickReloadBtn">
            <b-icon icon="arrow-clockwise"></b-icon> {{ $t("reload") }}
          </b-button>
          <b-button class="mr-4" variant="outline-secondary" v-b-modal:import-contacts-from-excel-file-modal>
            <b-icon icon="upload" class="mr-2"></b-icon> {{ $t("importFromFile") }}
          </b-button>
          <b-button class="mr-4" variant="outline-primary" v-b-modal:new-contact-modal>
            <b-icon icon="plus"></b-icon> {{ $t("createNew") }}
          </b-button>
          <b-button variant="outline-danger" @click="onClickDeleteAll">
            <b-icon icon="trash"></b-icon> {{ $t("deleteAll") }}
          </b-button>
        </div>
      </div>

      <div class="d-flex flex-row align-items-center justify-content-between mb-3">
        <div>
          <b-form class="d-inline-block" inline @submit.prevent="onClickSearch">
            <b-form-input class="mr-2" type="search" minlength="2" maxlength="200" v-model="searchText"
                          :placeholder="$t('adminSearchPlaceholder')"
                          style="min-width: 300px;" />
            <b-button type="submit" variant="primary">{{ $t("search") }}</b-button>
          </b-form>
        </div>
        <div class="d-flex flex-row align-items-center">
          <div v-if="totalItems" class="text-right flex-shrink-0 mr-3">{{ $t("resultCount", { count: totalItems }) }}</div>
          <b-pagination class="my-0 mr-3" v-model="currentPage" :total-rows="totalItems" :per-page="itemsPerPage"></b-pagination>
          <b-form-select v-model="itemsPerPage" :options="itemsPerPageSelections"></b-form-select>
        </div>
      </div>

      <b-table ref="table" :fields="tableFields" :items="tableItemProvider" hover primary-key="id" show-empty
               :per-page="itemsPerPage" :current-page="currentPage">
        <template v-slot:table-busy>
          <div class="text-center text-secondary py-4">{{ $t("loadingData") }}</div>
        </template>
        <template v-slot:empty>
          <div class="text-center text-secondary py-4">{{ $t("noData") }}</div>
        </template>
        <template v-slot:cell(imgUploadStatus)="data">
          <b-badge :variant="statusBadgeVariant[data.item.imgUploadStatus]">{{ data.item.imgUploadStatus }}</b-badge>
        </template>
        <template v-slot:cell(actions)="data">
          <b-button variant="outline-primary" size="sm" class="mr-3" @click="onClickEditContact(data.item)">{{ $t("update") }}</b-button>
          <b-button variant="outline-danger" size="sm" @click="onClickDeleteContact(data.item)">{{ $t("actionDelete") }}</b-button>
        </template>
      </b-table>

      <div class="d-flex flex-row justify-content-between">
          <b-button variant="link" :disabled="!totalItems" @click="onClickDownload">
            <b-icon-download class="mr-2"></b-icon-download> {{ $t("export") }}
          </b-button>
        </div>
    </div>

    <b-modal id="import-contacts-from-excel-file-modal" :title="$t('importContactFromFile')" hide-footer no-close-on-backdrop>
      <ImportContactsFromExcelFileModal modal-id="import-contacts-from-excel-file-modal" :activeFID="this.user.activeFaceId" @submitted="refreshData" />
    </b-modal>

    <b-modal id="new-contact-modal" :title="$t('addNewContact')" hide-footer no-close-on-backdrop>
      <NewContactModal modal-id="new-contact-modal" :activeFID="this.user.activeFaceId" @submitted="refreshData" />
    </b-modal>

    <b-modal id="edit-contact-modal" :title="$t('updateContact')" hide-footer no-close-on-backdrop>
      <EditContactModal modal-id="edit-contact-modal" :activeFID="this.user.activeFaceId" :contact="contactToEdit" @submitted="refreshData" />
    </b-modal>

  </main>
</template>

<style lang="scss" scoped>

</style>

<script lang="ts">
  import {Component, Vue, Ref} from 'vue-property-decorator';
  import _ from "lodash";
  import BaseComponent from "@/components/BaseComponent";
  import * as contactService from "@/service/contactService";
  import * as userService from "@/service/userService";

  import {BTable, BvTableCtxObject, BvTableProviderPromiseResult} from "bootstrap-vue/src/components/table";
  import NewContactModal from "@/components/contact/NewContactModal.vue";
  import EditContactModal from "@/components/contact/EditContactModal.vue";
  import {BvTableFieldArray} from "bootstrap-vue";
  import ImportContactsFromExcelFileModal from "@/components/contact/ImportContactsFromExcelFileModal.vue";


  @Component({
    components: {ImportContactsFromExcelFileModal, NewContactModal, EditContactModal }
  })
  export default class ContactList extends BaseComponent {
    user = userService.getCurrentUser() || null;
    
    tableFields: BvTableFieldArray = [
    
      {
        key: "name",
        label: this.$t("fullName").toString(),
        thClass: "text-nowrap",
      },
      {
        key: "email",
        label: "Email",
        thClass: "text-nowrap",
      },
      {
        key: "phone",
        label: this.$t("phoneNumber").toString(),
        thClass: "text-nowrap",
      },
      {
        key: "title",
        label: this.$t("workTitle").toString(),
        thClass: "text-nowrap",
      },
      {
        key: "department",
        label: this.$t("workDepartment").toString(),
        thClass: "text-nowrap",
      },
      {
        key: "organization",
        label: this.$t("workOrganization").toString(),
        thClass: "text-nowrap",
      },

    ];

    @Ref("table") table: BTable;
    readonly statusBadgeVariant = {
      FAILURE : "warning",
      SUCCESS : "success",
      NOT_AVAILABLE : "danger"
    };

    searchText = "";

    contacts = [];
    currentPage = 1;
    totalItems = 0;
    itemsPerPage = 50;
    itemsPerPageSelections = [
      { value: 10, text: "10 " + this.$t("itemsPerPage").toString() },
      { value: 20, text: "20 " + this.$t("itemsPerPage").toString() },
      { value: 50, text: "50 " + this.$t("itemsPerPage").toString() },
      { value: 100, text: "100 " + this.$t("itemsPerPage").toString() },
    ];

    contactToEdit = null;

    mounted() {
      let extraCol = [];
      if(this.user.activeFaceId) {
         extraCol = [ {
            key: "code",
            label: "Code",
            thClass: "text-nowrap",
          },
          {
            key: "imgUploadStatus",
            label: "Trạng thái",
            thClass: "text-nowrap",
          },
          {
            key: "actions",
            label: "",
            thClass: "text-nowrap",
            tdClass: "text-nowrap"
          }
        ]
      }
      else {
            extraCol = [
          {
            key: "actions",
            label: "",
            thClass: "text-nowrap",
            tdClass: "text-nowrap"
          }];
      }
      this.tableFields = this.tableFields.concat(extraCol);
    }

    onClickReloadBtn() {
      this.searchText = "";
      this.currentPage = 1;
      this.table.refresh();
    }

    async onClickDownload() {
      try {
        let customers = []; 
        customers = await contactService.getAllContactsForExport();
        
        const sheetData = customers.map(it => [
          it.name, it.title, it.department, it.organization, it.phone, it.email
        ]);
        sheetData.unshift(["Họ và tên", "Chức danh", "Phòng ban", "Đơn vị", "Di động", "Email"]);

        const XLSX = await import("xlsx");

        const workbook = XLSX.utils.book_new();
        const sheet = XLSX.utils.aoa_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(workbook, sheet);
        XLSX.writeFile(workbook, "DanhSachLienHe.xlsx");
      } catch (e) {
        console.error(e);
        this.toastError(e);
      }
    }

    onClickSearch() {
      this.table.refresh();
    }

    refreshData() {
      this.table.refresh();
    }

    async onClickDeleteAll() {
      const modalMessage = this.$t("deleteAllContactsConfirm").toString();
      const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirmDelete) {
        try {
          await contactService.deleteAllContacts();
          this.table.refresh();
        } catch (e) {
          this.toastError(e);

        }
      }
    }

    async tableItemProvider(ctx: BvTableCtxObject): Promise<BvTableProviderPromiseResult> {
      try {
        const res = await contactService.getAllContacts(ctx.currentPage, ctx.perPage, this.searchText?.trim());
        this.totalItems = res.total;
        return res.data;

      } catch (e) {
        this.toastError(e);
        return [];
      }
    }

    onClickEditContact(contact) {
      this.contactToEdit = contact;
      this.$bvModal.show("edit-contact-modal");
    }

    async onClickDeleteContact(contact) {
      const modalMessage = this.$t("deleteRecordConfirmation2", { name: contact.name, id: contact.id }).toString();
      const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirmDelete) {
        try {
          await contactService.deleteContact(contact.id);
          this.refreshData();
          this.toastSuccessDelay(this.$t("deleteContactSuccessful").toString());

        } catch (e) {
          this.toastError(e);
        }
      }
    }

  }
</script>
